import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LazyImage from './LazyImage';

const API = process.env.REACT_APP_BACKEND_URL;

const INITIAL_COUNT = 6;

const Portfolio = () => {
  const [categories, setCategories] = useState([]);
  const [allImages, setAllImages] = useState([]);
  const [activeCategory, setActiveCategory] = useState('automotive');
  const [lightbox, setLightbox] = useState(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  useEffect(() => {
    fetch(`${API}/api/portfolio/categories`).then(r => r.json()).then(setCategories).catch(() => {});
    fetch(`${API}/api/portfolio/images`).then(r => r.json()).then(setAllImages).catch(() => {});
  }, []);

  const activeImages = useMemo(() => {
    return allImages.filter(img => img.category_id === activeCategory);
  }, [activeCategory, allImages]);

  const visibleImages = useMemo(() => activeImages.slice(0, visibleCount), [activeImages, visibleCount]);
  const hasMore = visibleCount < activeImages.length;

  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    setVisibleCount(INITIAL_COUNT);
  };

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 md:py-32 lg:py-40 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="mb-16"
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] font-medium text-white/40 mb-4">My Work</p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight leading-tight text-white">
            Portfolio
          </h2>
        </motion.div>

        <div className="flex flex-wrap gap-3 md:gap-6 mb-12 md:mb-16" data-testid="portfolio-category-filters">
          {categories.map((cat) => (
            <button
              key={cat.id}
              data-testid={`gallery-filter-${cat.id}`}
              onClick={() => handleCategoryChange(cat.id)}
              className={`relative text-xs uppercase tracking-[0.2em] font-medium pb-2 transition-colors duration-300 ${
                activeCategory === cat.id ? 'text-white' : 'text-white/30 hover:text-white/60'
              }`}
            >
              {cat.name}
              {activeCategory === cat.id && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-[1px] bg-white"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5"
            data-testid="portfolio-grid"
          >
            {visibleImages.map((img, i) => (
              <motion.div
                key={img.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.25, 1, 0.5, 1] }}
                className="break-inside-avoid mb-4 md:mb-5 group relative cursor-pointer overflow-hidden border border-white/5"
                data-testid={`portfolio-image-${img.id}`}
                onClick={() => setLightbox(img)}
              >
                <LazyImage
                  src={img.image_url}
                  alt={img.title}
                  className="w-full block"
                  imgClassName="grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <p className="text-sm font-light tracking-wide text-white/80">{img.category_name}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {hasMore && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-12 text-center"
          >
            <button
              onClick={() => setVisibleCount(prev => prev + 6)}
              data-testid="load-more-btn"
              className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] font-medium text-white/50 hover:text-white transition-colors duration-300"
            >
              <span className="w-8 h-[1px] bg-white/20 group-hover:w-12 transition-all duration-500" />
              Load More ({activeImages.length - visibleCount} remaining)
              <span className="w-8 h-[1px] bg-white/20 group-hover:w-12 transition-all duration-500" />
            </button>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-8"
            data-testid="lightbox-overlay"
            onClick={() => setLightbox(null)}
          >
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              src={lightbox.image_url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain"
              data-testid="lightbox-image"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 text-white/60 hover:text-white text-2xl font-light transition-colors"
            >
              &times;
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
